const collectionRenderEvent = 'collection:render';
const collectionProductsUpdatedEvent = 'collection:products-updated';

function dispatchCollectionRender(detail) {
  document.dispatchEvent(new CustomEvent(collectionRenderEvent, { detail }));
}

function dispatchCollectionProductsUpdated() {
  document.dispatchEvent(new CustomEvent(collectionProductsUpdatedEvent));
}

async function fetchCollectionDoc(url, errorMessage) {
  const response = await fetch(url.toString());

  if (!response.ok) {
    throw new Error(`${errorMessage}: ${response.status}`);
  }

  const html = await response.text();

  return new DOMParser().parseFromString(html, 'text/html');
}

function preserveSearchParams(sourceUrl, targetUrl) {
  ['q', 'type', 'options[prefix]'].forEach((name) => {
    const value = sourceUrl.searchParams.get(name);

    if (value) {
      targetUrl.searchParams.set(name, value);
    }
  });
}

function setProductListLoading(isLoading) {
  const productList = document.querySelector('.collection-main .product-list');

  if (productList) {
    productList.style.opacity = isLoading ? '0.5' : '1';
  }
}

function getGridColumnCount(grid) {
  const columns = window.getComputedStyle(grid).gridTemplateColumns;

  if (!columns || columns === 'none') return 1;

  return Math.max(1, columns.split(' ').filter(Boolean).length);
}

function updateCollectionProductAnimationIndexes(scope = document) {
  const productLists = scope.querySelectorAll ? scope.querySelectorAll('.collection-main .product-list') : [];

  productLists.forEach((productList) => {
    const columnCount = getGridColumnCount(productList);

    productList.querySelectorAll('.product-item').forEach((item, index) => {
      item.style.setProperty('--animation-index', index % columnCount);
    });
  });
}

function updateCollectionCount(doc) {
  const currentCounts = document.querySelectorAll('.collection-utilities-count');
  const newCount = doc.querySelector('.collection-utilities-count');

  if (!newCount) return;

  currentCounts.forEach((count) => {
    count.textContent = newCount.textContent;
  });
}

function replaceProductList(doc) {
  const currentProductList = document.querySelector('.collection-main .product-list');
  const newProductList = doc.querySelector('.collection-main .product-list');
  const currentEmptyText = document.querySelector('.collection-main .collection-empty-text');
  const newEmptyText = doc.querySelector('.collection-main .collection-empty-text');

  if (!currentProductList || !newProductList) return;

  currentProductList.outerHTML = newProductList.outerHTML;

  if (currentEmptyText && newEmptyText) {
    currentEmptyText.outerHTML = newEmptyText.outerHTML;
  }

  CollectionColsSelector.applySavedCols(document.querySelector('.collection-main .product-list'));
  dispatchCollectionProductsUpdated();
}

function updateCollectionPagination(doc) {
  const currentInfinitePagination = document.querySelector('collection-infinite-pagination');
  const newInfinitePagination = doc.querySelector('collection-infinite-pagination');
  const currentPagination = document.querySelector('.collection-main .pagination-wrapper');
  const newPagination = doc.querySelector('.collection-main .pagination-wrapper');
  const productList = document.querySelector('.collection-main .product-list');

  if (currentInfinitePagination) {
    if (newInfinitePagination) {
      currentInfinitePagination.outerHTML = newInfinitePagination.outerHTML;
    } else {
      currentInfinitePagination.remove();
    }
    return;
  }

  if (currentPagination && newPagination) {
    currentPagination.outerHTML = newPagination.outerHTML;
  } else if (currentPagination) {
    currentPagination.remove();
  } else if (newPagination) {
    productList?.after(newPagination);
  }
}

let collectionAnimationIndexFrame = null;

function requestCollectionProductAnimationIndexUpdate() {
  if (collectionAnimationIndexFrame) return;

  collectionAnimationIndexFrame = window.requestAnimationFrame(() => {
    collectionAnimationIndexFrame = null;
    updateCollectionProductAnimationIndexes();
  });
}

updateCollectionProductAnimationIndexes();
window.addEventListener('resize', requestCollectionProductAnimationIndexUpdate);
document.addEventListener(collectionProductsUpdatedEvent, requestCollectionProductAnimationIndexUpdate);


class CollectionFilters extends HTMLElement {
  constructor() {
    super();
    this.priceDebounceTimer = null;
    this.announcementTimer = null;
    this.handleChange = this.handleChange.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleCollectionRender = this.handleCollectionRender.bind(this);
    this.handleViewResultsClick = this.handleViewResultsClick.bind(this);
    this.handleClearFiltersClick = this.handleClearFiltersClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('change', this.handleChange);
    this.addEventListener('input', this.handleInput);
    document.addEventListener(collectionRenderEvent, this.handleCollectionRender);
    this.initPriceRanges();
    this.initDialogFooterButtons();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('input', this.handleInput);
    document.removeEventListener(collectionRenderEvent, this.handleCollectionRender);
    this.viewResultsButton?.removeEventListener('click', this.handleViewResultsClick);
    this.clearFiltersButton?.removeEventListener('click', this.handleClearFiltersClick);
    clearTimeout(this.priceDebounceTimer);
    clearTimeout(this.announcementTimer);
  }

  handleChange(event) {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) return;
    if (target.closest('.collection-filter-price-range')) return;
    if (target.type !== 'checkbox' && target.type !== 'radio') return;

    this.updateCollection();
  }

  handleInput(event) {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) return;
    if (!target.closest('.collection-filter-price-range')) return;

    if (target.matches('[data-range-type]')) {
      this.syncPriceRangeInput(target);
    } else {
      this.syncPriceTextInput(target);
    }

    clearTimeout(this.priceDebounceTimer);

    this.priceDebounceTimer = setTimeout(() => {
      this.updateCollection();
    }, 800);
  }

  handleCollectionRender(event) {
    if (event.detail.source !== 'filters' && event.detail.source !== 'sort') return;

    this.updateFromDoc(event.detail.doc);
  }

  initPriceRanges() {
    this.querySelectorAll('.collection-filter-price-range').forEach((priceRange) => {
      this.syncPriceRange(priceRange, null, false);
    });
  }

  initDialogFooterButtons() {
    const dialog = this.closest('#collection-filters-dialog');

    if (!dialog) return;

    this.viewResultsButton = dialog.querySelector('[data-btn-view-results]');
    this.clearFiltersButton = dialog.querySelector('[data-btn-clear-filters]');

    this.viewResultsButton?.addEventListener('click', this.handleViewResultsClick);
    this.clearFiltersButton?.addEventListener('click', this.handleClearFiltersClick);
  }

  isInDialog() {
    return Boolean(this.closest('#collection-filters-dialog'));
  }

  handleViewResultsClick() {
    this.closeDialog();
  }

  handleClearFiltersClick() {
    this.clearFilters();
    this.updateCollection(this.getClearFiltersUrl());
  }

  getDialogHost() {
    return this.closest('theme-dialog');
  }

  closeDialog() {
    this.getDialogHost()?.requestClose();
  }

  clearFilters() {
    this.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      input.checked = false;
    });

    this.querySelectorAll('.collection-filter-price-inputs input').forEach((input) => {
      input.value = '';
    });

    this.initPriceRanges();
  }

  getClearFiltersUrl() {
    const currentUrl = new URL(window.location.href);
    const url = new URL(window.location.pathname, window.location.origin);
    const currentSort = currentUrl.searchParams.get('sort_by');

    preserveSearchParams(currentUrl, url);

    if (currentSort) {
      url.searchParams.set('sort_by', currentSort);
    }

    return url;
  }

  getPriceRangeParts(priceRange) {
    const minRange = priceRange.querySelector('[data-range-type="min"]');
    const maxRange = priceRange.querySelector('[data-range-type="max"]');
    const textInputs = priceRange.querySelectorAll('.collection-filter-price-inputs input');

    return {
      minInput: textInputs[0],
      maxInput: textInputs[1],
      minRange,
      maxRange,
    };
  }

  getNumber(value, fallback = 0) {
    if (value === null || value === undefined || value === '') return fallback;

    const number = Number(String(value).replace(',', '.'));

    return Number.isNaN(number) ? fallback : number;
  }

  getInputNumber(input, fallback = 0) {
    if (!input) return fallback;

    return this.getNumber(input.value, fallback);
  }

  clampPriceValue(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  syncPriceRangeInput(input) {
    const priceRange = input.closest('.collection-filter-price-range');

    if (!priceRange) return;

    this.syncPriceRange(priceRange, input.dataset.rangeType, true);
  }

  syncPriceTextInput(input) {
    const priceRange = input.closest('.collection-filter-price-range');

    if (!priceRange) return;

    this.syncPriceRange(priceRange, null, false);
  }

  syncPriceRange(priceRange, activeRangeType = null, updateTextInputs = false) {
    const { minInput, maxInput, minRange, maxRange } = this.getPriceRangeParts(priceRange);

    if (!minInput || !maxInput || !minRange || !maxRange) return;

    const minAllowed = this.getNumber(minRange.min, 0);
    const maxAllowed = this.getNumber(maxRange.max, this.getNumber(minRange.max, 0));
    const minInputHasValue = minInput.value.trim() !== '';
    const maxInputHasValue = maxInput.value.trim() !== '';
    const minFallback = minInputHasValue ? this.getInputNumber(minInput, minAllowed) : minAllowed;
    const maxFallback = maxInputHasValue ? this.getInputNumber(maxInput, maxAllowed) : maxAllowed;

    let minValue = activeRangeType === 'min'
      ? this.getInputNumber(minRange, minFallback)
      : this.getInputNumber(minInput, minFallback);
    let maxValue = activeRangeType === 'max'
      ? this.getInputNumber(maxRange, maxFallback)
      : this.getInputNumber(maxInput, maxFallback);

    minValue = this.clampPriceValue(minValue, minAllowed, maxAllowed);
    maxValue = this.clampPriceValue(maxValue, minAllowed, maxAllowed);

    if (minValue > maxValue) {
      if (activeRangeType === 'min') {
        maxValue = minValue;
      } else {
        minValue = maxValue;
      }
    }

    minRange.value = minValue;
    maxRange.value = maxValue;
    minRange.max = maxValue;
    maxRange.min = minValue;

    if ((updateTextInputs && activeRangeType === 'min') || minInputHasValue) {
      minInput.value = minValue;
    }

    if ((updateTextInputs && activeRangeType === 'max') || maxInputHasValue) {
      maxInput.value = maxValue;
    }
  }

  getFilterUrl() {
    const currentUrl = new URL(window.location.href);
    const url = new URL(window.location.pathname, window.location.origin);
    const currentSort = currentUrl.searchParams.get('sort_by');

    preserveSearchParams(currentUrl, url);

    if (currentSort) {
      url.searchParams.set('sort_by', currentSort);
    }

    this.querySelectorAll('input').forEach((input) => {
      if (input.disabled || !input.name) return;
      if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
      if (input.type !== 'checkbox' && input.type !== 'radio' && !input.value.trim()) return;

      url.searchParams.append(input.name, input.value.trim());
    });

    return url;
  }

  async updateCollection(url = this.getFilterUrl()) {
    setProductListLoading(true);

    try {
      const doc = await fetchCollectionDoc(url, 'Failed to filter collection');

      updateCollectionCount(doc);
      replaceProductList(doc);
      updateCollectionPagination(doc);
      dispatchCollectionRender({ doc, source: 'filters' });
      this.closeDialog();
      window.history.pushState({}, '', url.toString());
    } catch (error) {
      console.error(error);
      setProductListLoading(false);
    }
  }

  updateFromDoc(doc) {
    const isDialogFilters = this.isInDialog();
    const openDropdown = isDialogFilters
      ? null
      : this.querySelector('.collection-filter-dropdown .theme-dropdown-btn[aria-expanded="true"]')
        ?.closest('.collection-filter-dropdown');
    const openFilterParam = openDropdown?.dataset.filterParam;
    const newFilters = isDialogFilters
      ? doc.querySelector('#collection-filters-dialog collection-filters')
      : doc.querySelector('.collection-utilities-inline-filters collection-filters');

    if (!newFilters) return;

    this.outerHTML = newFilters.outerHTML;

    const updatedFilters = isDialogFilters
      ? document.querySelector('#collection-filters-dialog collection-filters')
      : document.querySelector('.collection-utilities-inline-filters collection-filters');

    if (isDialogFilters) {
      updatedFilters?.updateDialogFooter(doc);
    } else if (openFilterParam) {
      updatedFilters?.restoreOpenDropdown(openFilterParam);
    }

    updatedFilters?.announceResults(doc);
  }

  restoreOpenDropdown(filterParam) {
    window.requestAnimationFrame(() => {
      const dropdown = this.querySelector(`.collection-filter-dropdown[data-filter-param="${CSS.escape(filterParam)}"]`);

      if (!dropdown) return;

      if (typeof dropdown.open === 'function') {
        dropdown.open();
        return;
      }

      const button = dropdown.querySelector('.theme-dropdown-btn');
      const content = dropdown.querySelector('.theme-dropdown-content');

      button?.setAttribute('aria-expanded', 'true');

      if (content) {
        content.hidden = false;
      }
    });
  }

  updateDialogFooter(doc) {
    const dialog = this.closest('#collection-filters-dialog');
    const newDialog = doc.querySelector('#collection-filters-dialog');

    if (!dialog || !newDialog) return;

    const currentViewResultsButton = dialog.querySelector('[data-btn-view-results]');
    const newViewResultsButton = newDialog.querySelector('[data-btn-view-results]');

    if (currentViewResultsButton && newViewResultsButton) {
      currentViewResultsButton.innerHTML = newViewResultsButton.innerHTML;
      currentViewResultsButton.setAttribute(
        'aria-label',
        newViewResultsButton.getAttribute('aria-label') || newViewResultsButton.textContent.trim()
      );
    }
  }

  announceResults(doc) {
    const liveRegion = this.querySelector('[data-collection-live-region]');
    const newCountText = doc.querySelector('[data-collection-live-region]')?.dataset.countText;

    if (!liveRegion || !newCountText) return;

    const messageTemplate = liveRegion.dataset.text || '';
    liveRegion.dataset.countText = newCountText;
    liveRegion.textContent = '';

    clearTimeout(this.announcementTimer);

    this.announcementTimer = setTimeout(() => {
      liveRegion.textContent = messageTemplate.replace('[count]', newCountText);
    }, 100);
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}

class CollectionSortBy extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.handleCollectionRender = this.handleCollectionRender.bind(this);
  }

  connectedCallback() {
    this.addEventListener('change', this.handleChange);
    document.addEventListener(collectionRenderEvent, this.handleCollectionRender);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    document.removeEventListener(collectionRenderEvent, this.handleCollectionRender);
  }

  handleChange(event) {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== 'sort_by') return;

    this.updateCollection(target.value);
  }

  getSortUrl(sortBy) {
    const currentUrl = new URL(window.location.href);
    const url = new URL(window.location.pathname, window.location.origin);

    preserveSearchParams(currentUrl, url);

    currentUrl.searchParams.forEach((value, key) => {
      if (key.startsWith('filter.') || key === 'sort_by') {
        url.searchParams.append(key, value);
      }
    });

    url.searchParams.set('sort_by', sortBy);

    return url;
  }

  async updateCollection(sortBy) {
    const url = this.getSortUrl(sortBy);

    setProductListLoading(true);

    try {
      const doc = await fetchCollectionDoc(url, 'Failed to sort collection');

      updateCollectionCount(doc);
      replaceProductList(doc);
      updateCollectionPagination(doc);
      dispatchCollectionRender({ doc, source: 'sort' });
      window.history.pushState({}, '', url.toString());
      this.closest('theme-dropdown')?.close?.();
    } catch (error) {
      console.error(error);
      setProductListLoading(false);
    }
  }

  handleCollectionRender(event) {
    const newSort = event.detail.doc.querySelector('collection-sort-by');

    if (newSort) {
      this.innerHTML = newSort.innerHTML;
    }
  }
}

if (!customElements.get('collection-sort-by')) {
  customElements.define('collection-sort-by', CollectionSortBy);
}

class CollectionInfinitePagination extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleCollectionRender = this.handleCollectionRender.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.infinite-pagination-btn');
    this.status = this.querySelector('#infinite-pagination-status');
    document.addEventListener(collectionRenderEvent, this.handleCollectionRender);

    if (!this.button) return;

    this.button.addEventListener('click', this.handleClick);
    this.initObserver();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
    this.observer?.disconnect();
    document.removeEventListener(collectionRenderEvent, this.handleCollectionRender);
  }

  handleClick(event) {
    event.preventDefault();
    this.loadNextPage();
  }

  initObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(this.handleIntersection, {
      rootMargin: '0px 0px -100px 0px',
    });
    this.observer.observe(this);
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      this.loadNextPage();
    });
  }

  handleCollectionRender(event) {
    if (event.detail.source === 'infinite-pagination') return;

    this.updateFromDoc(event.detail.doc);
  }

  async loadNextPage() {
    if (!this.button || this.button.getAttribute('aria-disabled') === 'true') return;

    const url = new URL(this.button.href);

    this.setLoadingState(true);
    this.observer?.disconnect();

    try {
      const doc = await fetchCollectionDoc(url, 'Failed to load collection page');

      this.appendProducts(doc);
      this.announceLoadedItems();
      this.updateFromDoc(doc);
      window.history.pushState({}, '', url.toString());
    } catch (error) {
      console.error(error);
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    this.button?.classList.toggle('loading', isLoading);
    this.button?.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
  }

  appendProducts(doc) {
    const currentProductList = document.querySelector('.collection-main .product-list');
    const newProductItems = doc.querySelectorAll('.collection-main .product-list .product-item');

    if (!currentProductList || !newProductItems.length) return;

    newProductItems.forEach((item) => {
      currentProductList.append(item);
    });

    CollectionColsSelector.applySavedCols(currentProductList);
    dispatchCollectionProductsUpdated();
    dispatchCollectionRender({ doc, source: 'infinite-pagination' });
  }

  announceLoadedItems() {
    if (!this.status || !this.button?.dataset.textStatus) return;

    this.status.textContent = this.button.dataset.textStatus;
  }

  updateFromDoc(doc) {
    const newPagination = doc.querySelector('collection-infinite-pagination');

    if (newPagination) {
      this.outerHTML = newPagination.outerHTML;
    } else {
      this.remove();
    }
  }
}

if (!customElements.get('collection-infinite-pagination')) {
  customElements.define('collection-infinite-pagination', CollectionInfinitePagination);
}

class CollectionColsSelector extends HTMLElement {
  static storagePrefix = 'collection-cols-selector';
  static breakpoints = [
    {
      name: 'mobile',
      cssVar: '--cols-mobile',
      mediaQuery: '(max-width: 599px)',
      min: 1,
      max: 3,
    },
    {
      name: 'tablet',
      cssVar: '--cols-tablet',
      mediaQuery: '(min-width: 600px) and (max-width: 1199px)',
      min: 1,
      max: 4,
    },
    {
      name: 'desktop',
      cssVar: '--cols-desktop',
      mediaQuery: '(min-width: 1200px)',
      min: 2,
      max: 6,
    },
  ];

  constructor() {
    super();
    this.handleInput = this.handleInput.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleCollectionProductsUpdated = this.handleCollectionProductsUpdated.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="range"]');
    this.text = this.querySelector('p');

    if (!this.input) return;

    this.input.addEventListener('input', this.handleInput);
    window.addEventListener('resize', this.handleResize);
    document.addEventListener(collectionProductsUpdatedEvent, this.handleCollectionProductsUpdated);
    this.updateInput();
    this.updateCols();
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.handleInput);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener(collectionProductsUpdatedEvent, this.handleCollectionProductsUpdated);
  }

  handleInput() {
    this.saveCols();
    this.updateCols();
    this.updateText();
  }

  handleResize() {
    this.updateInput();
    this.updateCols();
    this.updateText();
  }

  handleCollectionProductsUpdated() {
    this.updateCols();
  }

  getActiveBreakpoint() {
    return CollectionColsSelector.breakpoints.find((breakpoint) =>
      window.matchMedia(breakpoint.mediaQuery).matches
    ) || CollectionColsSelector.breakpoints[CollectionColsSelector.breakpoints.length - 1];
  }

  getStorageKey(breakpoint) {
    return `${CollectionColsSelector.storagePrefix}-${breakpoint.name}`;
  }

  getDefaultCols(breakpoint) {
    const railDefault = CollectionColsSelector.getRailDefaultCols(breakpoint);

    if (railDefault) return railDefault;

    const productGrid = document.querySelector('.collection-main .product-list');
    const cssValue = productGrid
      ? getComputedStyle(productGrid).getPropertyValue(breakpoint.cssVar).trim()
      : '';

    return cssValue || this.input.value || this.input.min;
  }

  updateInput() {
    const breakpoint = this.getActiveBreakpoint();
    const cols = this.getSavedCols(breakpoint) || this.getDefaultCols(breakpoint);

    this.input.min = breakpoint.min;
    this.input.max = breakpoint.max;
    this.input.value = cols;
    this.updateText();
  }

  getSavedCols(breakpoint) {
    return CollectionColsSelector.readSavedCols(breakpoint);
  }

  isValidCols(cols, breakpoint) {
    return CollectionColsSelector.isValidCols(cols, breakpoint);
  }

  saveCols() {
    /* In the theme editor the merchant's own clicking must not be remembered,
       or the rail's "Default view" setting would look like it does nothing on
       the next reload. */
    if (CollectionColsSelector.isDesignMode()) return;

    const breakpoint = this.getActiveBreakpoint();

    try {
      window.localStorage.setItem(this.getStorageKey(breakpoint), this.input.value);
    } catch {
      // Ignore storage failures so the selector still works for the current page.
    }
  }

  updateCols() {
    const productGrid = document.querySelector('.collection-main .product-list');

    CollectionColsSelector.breakpoints.forEach((breakpoint) => {
      const cols = this.getSavedCols(breakpoint) || this.getDefaultCols(breakpoint);

      productGrid?.style.setProperty(breakpoint.cssVar, cols);
    });

    requestCollectionProductAnimationIndexUpdate();
  }

  updateText() {
    if (!this.text || !this.input) return;

    const label = Number(this.input.value) === 1 ? this.dataset.textSingle : this.dataset.textMultiple;

    this.text.textContent = `${this.input.value} ${label}`;
  }

  static isDesignMode() {
    return document.documentElement.dataset.designMode === 'true';
  }

  static isValidCols(cols, breakpoint) {
    const value = Number(cols);
    const min = Number(breakpoint.min);
    const max = Number(breakpoint.max);

    return !Number.isNaN(value) && value >= min && value <= max;
  }

  static readSavedCols(breakpoint) {
    if (CollectionColsSelector.isDesignMode()) return null;

    try {
      const cols = window.localStorage.getItem(`${CollectionColsSelector.storagePrefix}-${breakpoint.name}`);

      if (!cols) return null;
      if (!CollectionColsSelector.isValidCols(cols, breakpoint)) return null;

      return cols;
    } catch {
      return null;
    }
  }

  /* The rail's "Default view" setting is the merchant's chosen landing layout.
     It only covers desktop, since the switcher is hidden on mobile and tablet
     keeps whatever the grid block is set to. */
  static getRailDefaultCols(breakpoint) {
    if (breakpoint.name !== 'desktop') return null;

    const selector = document.querySelector('collection-cols-selector[data-default-cols]');
    const cols = selector?.dataset.defaultCols;

    if (!cols) return null;
    if (!CollectionColsSelector.isValidCols(cols, breakpoint)) return null;

    return cols;
  }

  /* Called after an AJAX filter, sort or infinite page. The replaced markup
     carries the grid block's own column settings, so the shopper's saved view
     and the rail default both have to be laid back over it. */
  static applySavedCols(productGrid) {
    if (!productGrid) return;

    CollectionColsSelector.breakpoints.forEach((breakpoint) => {
      const cols =
        CollectionColsSelector.readSavedCols(breakpoint) || CollectionColsSelector.getRailDefaultCols(breakpoint);

      if (!cols) return;

      productGrid.style.setProperty(breakpoint.cssVar, cols);
    });
  }
}

if (!customElements.get('collection-cols-selector')) {
  customElements.define('collection-cols-selector', CollectionColsSelector);
}
